import axios from "axios";
import { getTokens, setTokens, clearTokens } from "./tokens";
import { refreshTokenAPI } from "../api/userApi";

const instance = axios.create({
  // withCredentials: true,
  baseURL: process.env.REACT_APP_SERVER_URL,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

// Function to process failed requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Function to refresh tokens
const refreshTokens = async () => {
  const tokens = getTokens();

  if (!tokens?.refresh_token) {
    throw new Error('Session expired. Please login again.');
  }

  try {
    const response = await refreshTokenAPI(tokens.refresh_token);

    if (response.success) {
      const newTokens = response.tokens;

      // Update tokens in storage (тільки JWT токени)
      setTokens({
        access: newTokens.access,
        refresh: newTokens.refresh,
      });

      return {
        access: newTokens.access,
      };
    } else {
      throw new Error('Token refresh failed');
    }
  } catch (error) {
    // Clear tokens and redirect to login
    clearTokens();
    localStorage.removeItem('user');
    sessionStorage.clear();
    window.location.href = '/login';
    throw error;
  }
};

// // Request interceptor to automatically add tokens to headers
instance.interceptors.request.use(
  (config) => {
    // Get tokens using centralized function
    const tokens = getTokens();

    // Add custom token to Authorization header if available
    if (tokens?.access_token) {
      // config.headers["Content-Type"] = "application/json";
      config.headers[process.env.REACT_APP_AUTH_TOKEN] = `Bearer ${tokens.access_token}`;
      config.headers["ngrok-skip-browser-warning"] = "true";
    }

    // Add Firebase token to custom header if available
    if (tokens?.firebase_token) {
      config.headers[process.env.REACT_APP_FIREBASE_AUTH_TOKEN] = `Bearer ${tokens.firebase_token}`;
    }

    return config;
  },
  (error) => {
    // Handle request errors
    return Promise.reject(error);
  }
);

// // Response interceptor to handle 401 unauthorized responses and token refresh
instance.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 unauthorized responses
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(tokens => {
          originalRequest.headers[process.env.REACT_APP_AUTH_TOKEN] = `Bearer ${tokens.access}`;
          originalRequest.headers[process.env.REACT_APP_FIREBASE_AUTH_TOKEN] = `Bearer ${tokens.firebase}`;
          return instance(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newTokens = await refreshTokens();
        processQueue(null, newTokens);

        // Retry the original request with new tokens
        originalRequest.headers[process.env.REACT_APP_AUTH_TOKEN] = `Bearer ${newTokens.access}`;
        originalRequest.headers[process.env.REACT_APP_FIREBASE_AUTH_TOKEN] = `Bearer ${newTokens.firebase}`;
        return instance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Do not force-logout on every 401.
    // Some endpoints can legitimately return 401/403 for non-admin users.
    // Real session expiration is already handled by refresh flow above.

    // Return the error to be handled by the calling code
    return Promise.reject(error);
  }
);

export const getAxiosInstance = () => {
  return instance;
};
